import React from 'react';
import { useTranslation } from 'react-i18next';
import { Icon } from '@iconify/react';
import { EmptyState, NumberValue, Widget } from '@heroui-pro/react';
import { PieChart } from '@heroui-pro/react/pie-chart';
import type { PopularBrands } from '@/api/models';
import { formatCurrencyValue } from '@/utils/currency';
import { getNodeTestIdProp } from '@/utils/nodeTestProps';
import { RefLabel } from '@/components/refLabel';
import { PopularBrandsTooltip } from './PopularBrandsTooltip';
import { getBrandColor } from './popularBrandsMeta';
import { formatGel } from '@/utils/money';

type PopularBrandsViewProps = {
    data: PopularBrands;
};

export const PopularBrandsView: React.FC<PopularBrandsViewProps> = ({
    data,
}) => {
    const { t } = useTranslation('pages');

    const isEmpty = data.items.length === 0;

    const copy = React.useMemo(
        () => ({
            title: t('sales.widgets.popularBrands.title'),
            subtitle: t('sales.widgets.popularBrands.subtitle'),
            chartAria: t('sales.widgets.popularBrands.chartAria'),
            emptyTitle: t('sales.widgets.popularBrands.empty.title'),
            emptyDescription: t(
                'sales.widgets.popularBrands.empty.description',
            ),
        }),
        [t],
    );

    const formatRevenue = React.useCallback(
        (value: number | string) => formatCurrencyValue(data.currency, value),
        [data.currency],
    );

    return (
        <section
            aria-labelledby="popular-brands-widget-title"
            {...getNodeTestIdProp('popular-brands-section')}
        >
            <Widget
                className="flex flex-col gap-4"
                {...getNodeTestIdProp('popular-brands-widget')}
            >
                <Widget.Header
                    className="mt-4"
                    {...getNodeTestIdProp('popular-brands-widget-header')}
                >
                    <Widget.Title
                        className="flex items-center gap-2"
                        {...getNodeTestIdProp('popular-brands-widget-title')}
                    >
                        <div
                            aria-hidden
                            className="flex size-8 shrink-0 items-center justify-center rounded-lg bg-accent/10 text-accent"
                            {...getNodeTestIdProp('popular-brands-icon-wrap')}
                        >
                            <Icon
                                className="size-4"
                                icon="solar:star-bold-duotone"
                                {...getNodeTestIdProp('popular-brands-icon')}
                            />
                        </div>
                        {copy.title}
                    </Widget.Title>
                    <Widget.Description
                        {...getNodeTestIdProp('popular-brands-widget-subtitle')}
                    >
                        {copy.subtitle}
                    </Widget.Description>
                </Widget.Header>
                <Widget.Content
                    className={
                        isEmpty
                            ? 'flex min-h-55 items-center justify-center'
                            : 'flex flex-col gap-6 sm:flex-row sm:items-start'
                    }
                >
                    {isEmpty ? (
                        <EmptyState
                            {...getNodeTestIdProp('popular-brands-empty')}
                        >
                            <EmptyState.Header>
                                <EmptyState.Media variant="icon">
                                    <Icon
                                        aria-hidden
                                        className="size-6"
                                        icon="solar:star-bold-duotone"
                                        {...getNodeTestIdProp(
                                            'popular-brands-empty-icon',
                                        )}
                                    />
                                </EmptyState.Media>
                                <EmptyState.Title
                                    {...getNodeTestIdProp(
                                        'popular-brands-empty-title',
                                    )}
                                >
                                    {copy.emptyTitle}
                                </EmptyState.Title>
                                <EmptyState.Description
                                    {...getNodeTestIdProp(
                                        'popular-brands-empty-description',
                                    )}
                                >
                                    {copy.emptyDescription}
                                </EmptyState.Description>
                            </EmptyState.Header>
                        </EmptyState>
                    ) : (
                        <>
                            <div
                                aria-label={copy.chartAria}
                                className="mx-auto size-47.5 shrink-0 sm:mx-0"
                                role="img"
                                {...getNodeTestIdProp('popular-brands-chart')}
                            >
                                <PieChart height={190} width="100%">
                                    <PieChart.Pie
                                        cx="50%"
                                        cy="50%"
                                        data={data.items}
                                        dataKey="revenue"
                                        innerRadius={0}
                                        isAnimationActive={false}
                                        nameKey="brandName"
                                        outerRadius={90}
                                        stroke="none"
                                    >
                                        {data.items.map((brand, index) => (
                                            <PieChart.Cell
                                                fill={getBrandColor(index)}
                                                key={brand.brandId}
                                            />
                                        ))}
                                    </PieChart.Pie>
                                    <PieChart.Tooltip
                                        content={
                                            <PopularBrandsTooltip
                                                valueFormatter={formatRevenue}
                                            />
                                        }
                                        cursor={false}
                                    />
                                </PieChart>
                            </div>
                            <ul
                                aria-label={copy.title}
                                className="-mr-2 flex w-full min-w-0 flex-col gap-2 overflow-y-auto rounded-md pr-2 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-focus sm:max-h-47.5 sm:flex-1"
                                tabIndex={0}
                                {...getNodeTestIdProp('popular-brands-legend')}
                            >
                                {data.items.map((brand, index) => (
                                    <li
                                        key={brand.brandId}
                                        className="flex items-center justify-between gap-2 py-0.5"
                                        {...getNodeTestIdProp(
                                            `popular-brands-item-${brand.brandId}`,
                                        )}
                                    >
                                        <div
                                            className="flex min-w-0 items-center gap-2"
                                            {...getNodeTestIdProp(
                                                `popular-brands-item-label-root-${brand.brandId}`,
                                            )}
                                        >
                                            <span
                                                aria-hidden
                                                className="size-2.5 shrink-0 rounded-sm"
                                                style={{
                                                    backgroundColor:
                                                        getBrandColor(index),
                                                }}
                                                {...getNodeTestIdProp(
                                                    `popular-brands-item-swatch-${brand.brandId}`,
                                                )}
                                            />
                                            <span
                                                className="truncate text-xs text-muted"
                                                {...getNodeTestIdProp(
                                                    `popular-brands-item-name-${brand.brandId}`,
                                                )}
                                            >
                                                <RefLabel
                                                    deleted={brand.deleted}
                                                    id={brand.brandId}
                                                    name={brand.brandName}
                                                />
                                            </span>
                                        </div>
                                        <div
                                            className="flex shrink-0 items-center gap-2"
                                            {...getNodeTestIdProp(
                                                `popular-brands-item-meta-root-${brand.brandId}`,
                                            )}
                                        >
                                            {/* `formatGel` on the tetri twin, not Intl's currency style: the latter renders `GEL 455` while every other money figure in the app renders `₾ 455` (BOS-171). */}
                                            <span
                                                className="text-xs font-semibold text-foreground tabular-nums"
                                                {...getNodeTestIdProp(
                                                    `popular-brands-item-revenue-${brand.brandId}`,
                                                )}
                                            >
                                                {formatGel(brand.revenueTetri)}
                                            </span>
                                            <span
                                                className="w-9 shrink-0 text-right text-[10px] text-muted"
                                                {...getNodeTestIdProp(
                                                    `popular-brands-item-share-${brand.brandId}`,
                                                )}
                                            >
                                                <NumberValue
                                                    maximumFractionDigits={0}
                                                    style="decimal"
                                                    value={brand.sharePercent}
                                                    {...getNodeTestIdProp(
                                                        `popular-brands-item-share-percent-${brand.brandId}`,
                                                    )}
                                                />
                                                %
                                            </span>
                                        </div>
                                    </li>
                                ))}
                            </ul>
                        </>
                    )}
                </Widget.Content>
            </Widget>
        </section>
    );
};
